import { handleResponseErrors } from '@restDataAccess/handleResponseErrors';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { authorizationHeader } from '../authorizationHeader.ts';
import { useClearCacheCallback } from './cacheHooks.ts';
import { assertApiBaseForFetch } from './getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from './useGetApiUrl.ts';

type postFormDataProps<T> = {
  baseApiRoute: ApiNameType;
  cacheKeys: string[];
  headers?: HeadersInit;
  identifier?: string;
  returnJsonResult?: boolean;
  route: string;
  /** Build the multipart body from the mutation variables (files + form fields). */
  toFormData: (variables: T) => FormData;
};

/**
 * POST `multipart/form-data` (file/image upload) with bearer auth and cache invalidation.
 *
 * Sibling of {@link useAuthApiPost}, which always JSON-serializes its body — unusable for
 * uploads. Note that `Content-Type` is deliberately NOT set: the browser must add it
 * together with the multipart boundary, and setting it by hand breaks the request.
 */
export const useAuthApiPostFormData = <T, V = void>({
  baseApiRoute,
  cacheKeys,
  headers,
  identifier,
  returnJsonResult = true,
  route,
  toFormData,
}: postFormDataProps<T>) => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);
  const clearCacheCallback = useClearCacheCallback(cacheKeys);

  const postFormData = async (variables: T): Promise<V> => {
    try {
      const base = assertApiBaseForFetch(api);
      const response = await fetch(`${base}${route}`, {
        method: 'POST',
        body: toFormData(variables),
        headers: {
          ...authorizationHeader(auth.user),
          ...headers,
        },
      });

      return await handleResponseErrors<V>(response, identifier, returnJsonResult);
    } catch (error) {
      console.error(`Failed to upload ${identifier}`, error);
      throw error;
    }
  };

  return useMutation({
    mutationFn: postFormData,
    onSuccess: () => clearCacheCallback(),
  });
};
